/**
 * Get a query parameter from the URL.
 * @param name - The name of the query parameter.
 * @returns The value of the query parameter.
 *
 * @example
 * getQueryParam("key");
 * // Returns "value"
 */
export const getQueryParam = (name: string) => {
  return new URLSearchParams(window.location.search).get(name) || undefined;
};

/**
 * Build a query string from a list of keys.
 * @param keys - The keys to build the query string from.
 * @returns The query string.
 *
 * @example
 * buildQueryString(["key1", "key2", "key3"]);
 * // Returns "key1=value1&key2=value2&key3=value3"
 */
export const buildQueryString = (keys: string[]): string => {
  return keys
    .map((key) => {
      let value = getQueryParam(key);
      if (value && value.endsWith("?null")) {
        value = value.slice(0, -5);
      }
      const encodedValue = value ? encodeURIComponent(value) : "";
      return value ? `${key}=${encodedValue}` : null;
    })
    .filter((pair) => pair !== null)
    .join("%26");
};

/**
 * Builds an href with query parameters.
 * @param basePath - The base path for the href.
 * @param queryParams - Optional query parameters to append.
 * @param defaultQuery - Optional default query string to use if queryParams is empty.
 * @returns The complete href with query parameters.
 *
 * @example
 * buildHref("/login", "next=home&region=us");
 * // Returns "/login?next=home&region=us"
 *
 * @example
 * buildHref("/login", "", "next=%2Fus%2F");
 * // Returns "/login?next=%2Fus%2F"
 */
export const buildHref = (
  basePath: string,
  queryParams?: string,
  defaultQuery?: string,
) => {
  const query = queryParams || defaultQuery || "";
  return `${basePath}${query ? `?${query}` : ""}`;
};
